import { BreadcrumbsProps } from "components/BreadCrumb";
import { Form } from "components/Form";
import { Page } from "components/Page";
import { Stepper, StepProps } from "components/Steps";
import { YupCore } from "core/yup";
import { PropertyTypeForm } from "./PropertyTypeForm";
import { CreditDataForm } from "./CreditDataForm";
import { ResultCredit } from "pages/SimulatorCredit/ResultCredit";
import { useMemo } from "react";
import { PersonalDataForm } from "./PersonalDataForm";
import { isValidCpf } from "utils/validators";
import * as DateFns from "date-fns";

export interface SimulateCreditData {
  city: string;
  countMonths: string;
  cpf: string;
  dateBirthday: string;
  deadline: string;
  email: string;
  approximateAmount: string;
  financingAmount: string;
  name: string;
  phoneNumber: string;
  propertyType: string;
}

export function SimulatorCredit() {
  const steps = useMemo(() => {
    const values: StepProps[] = [
      { title: "Tipo de Imóvel", content: <PropertyTypeForm /> },
      {
        title: "Informações sobre o crédito",
        content: <CreditDataForm />,
      },
      { title: "Dados pessoais", content: <PersonalDataForm /> },
      { title: "Resultado", content: <ResultCredit /> },
    ];
    return values;
  }, []);

  const validateCreditWithPropertyAmount = useMemo(() => {
    return {
      test: function (value: any, ctx: any) {
        const propertyAmount = ctx.parent.propertyAmount;
        const creditAmount = value;

        if (!creditAmount) {
          return true;
        }

        const amountConverted =
          Number(propertyAmount) - Number(propertyAmount) * 0.4; //valor do emprestimo nao pode passar dos 80% do imóvel

        if (amountConverted < Number(creditAmount)) {
          return false;
        }

        return true;
      },
      message: `O valor do empréstimo esta limitado a 60% do valor do imóvel`,
    };
  }, []);

  const formSchema = useMemo(() => {
    return YupCore.object().shape({
      city: YupCore.object().required(),
      email: YupCore.string().email().required(),
      name: YupCore.string().required(),
      dateBirthday: YupCore.date()
        .required()
        .nullable()
        .max(
          DateFns.subYears(new Date(), 18),
          "É necessário ter no minímo 18 anos"
        )
        .min(
          DateFns.subYears(new Date(), 77),
          "É necessário ter menos de 77 anos"
        ),
      cpf: YupCore.string()
        .nullable(true)
        .test("invalid-cnpj", "CPF inválido", function (value: any) {
          return !value ? true : isValidCpf(value);
        }),
      phoneNumber: YupCore.string().required(),
      propertyAmount: YupCore.number()
        .min(7000000, "Valor mínimo: 70.000,00")
        .max(1000000000, "Valor máximo: 10.000.000,00")
        .required(),
      creditAmount: YupCore.number()
        .min(7000000, "Valor mínimo: 70.000,00")
        .test(validateCreditWithPropertyAmount)
        .max(1000000000, "Valor máximo: 10.000.000,00")
        .required(),
      deadline: YupCore.object().required(),
    });
  }, [validateCreditWithPropertyAmount]);

  const breadcrumbs: BreadcrumbsProps = useMemo(() => {
    return {
      paths: [
        {
          link: "/",
          title: "Página inicial",
        },
        {
          link: "/modalidade",
          title: "Modalidade",
        },
      ],
      currentPage: "Crédito com imóvel em garantia",
    };
  }, []);

  return (
    <Page breadcrumbs={breadcrumbs}>
      <Form
        initialSchemaValidation={formSchema}
        initialValues={{ countMonths: 12 }}
        onSubmit={(values) => console.log(values)}
      >
        <Stepper steps={steps} />
      </Form>
    </Page>
  );
}
